/**
 * Questions & FAQs — every question customers asked, as a sheet, and the FAQs built from them; plus every upsell
 * the crew sent in (owner, Nov 1 2026: "are all upsells saved somewhere? Also turn all asked questions into a sheet
 * and turn into common FAQs").
 *
 * The sheet is the app's own record (services/textAsks → services/faq): their words, what the app understood, the
 * first reply we sent. "Build the FAQs" groups them (Claude when the key is there, by topic otherwise); each FAQ is
 * editable, and an edited one survives the next build. Nothing here texts anyone.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Download, HelpCircle, RefreshCw, Plus, Trash2, Pencil, Check, X, Camera } from 'lucide-react';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

type Row = { id: number; phone: string; phone_pretty: string; kind: string; summary: string; status: string; asked_at: string; customer_id: number | null; customer_name: string | null; question: string; answer: string | null };
type Faq = { id: number; question: string; answer: string; count: number; example_ids: number[]; source: string; is_public: number; edited_at: string | null };
type Upsell = { id: number; customer_id: number; customer_name?: string; name?: string; note: string; status: string; created_at: string; photo_urls?: string[]; address?: string; est_value?: number | null };

const day = (ts: string) => { const d = new Date(String(ts).replace(' ', 'T')); return Number.isNaN(d.getTime()) ? String(ts).slice(0, 10) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
const KIND_WORD: Record<string, string> = { question: 'Question', other: 'Request', complaint: 'Complaint' };

type Draft = { id: number | null; question: string; answer: string };
function Editor({ value, onSave, onCancel, busy }: { value: Draft; onSave: (d: Draft) => void; onCancel: () => void; busy: boolean }) {
  const [q, setQ] = useState(value.question); const [a, setA] = useState(value.answer);
  return (
    <div className="rounded-xl border border-brand-300 bg-brand-50 p-3 space-y-2">
      <input className="input text-sm w-full" value={q} onChange={e => setQ(e.target.value)} placeholder="The question, the way a customer asks it" />
      <textarea className="input text-sm w-full" rows={3} value={a} onChange={e => setA(e.target.value)} placeholder="The answer, in your words" />
      <div className="flex gap-2">
        <button type="button" onClick={() => onSave({ id: value.id, question: q, answer: a })} disabled={busy || !q.trim()} className="btn-primary text-xs py-1 px-3 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Save</button>
        <button type="button" onClick={onCancel} className="btn-secondary text-xs py-1 px-3 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel</button>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [tab, setTab] = useState<'faqs' | 'sheet' | 'upsells'>('faqs');
  const [days, setDays] = useState(365);
  const { data: sheet } = useQuery(['faq-questions', days], () => api.get(`/faq/questions?days=${days}`).then(r => r.data as { rows: Row[]; ai: boolean }), { staleTime: 60_000 });
  const { data: faqData } = useQuery('faqs', () => api.get('/faq').then(r => r.data as { faqs: Faq[]; ai: boolean }), { staleTime: 60_000 });
  const { data: ups } = useQuery('upsells-all', () => api.get('/reminders/upsells').then(r => r.data as { upsells: Upsell[] }), { staleTime: 60_000 });
  const rows = sheet?.rows || [];
  const faqs = faqData?.faqs || [];
  const upsells = ups?.upsells || [];

  const build = useMutation(() => api.post('/faq/build', { days }).then(r => r.data), {
    onSuccess: d => { toast.success(d.message, { autoClose: 8000 }); qc.invalidateQueries('faqs'); setTab('faqs'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not build the FAQs'); },
  });
  const remove = useMutation((id: number) => api.delete(`/faq/${id}`), { onSuccess: () => qc.invalidateQueries('faqs') });
  const save = useMutation(({ id, question, answer }: { id: number | null; question: string; answer: string }) => id ? api.put(`/faq/${id}`, { question, answer }) : api.post('/faq', { question, answer }), {
    onSuccess: () => { qc.invalidateQueries('faqs'); setEditing(null); setAdding(false); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save'); },
  });
  const [editing, setEditing] = useState<Draft | null>(null);
  const [adding, setAdding] = useState(false);

  async function downloadCsv() {
    try {
      const res = await api.get(`/faq/questions.csv?days=${days}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a');
      a.href = url; a.download = 'questions-asked.csv'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the sheet'); }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><HelpCircle className="h-5 w-5 text-brand-600" /> Questions & FAQs</h1>
        <div className="flex items-center gap-2 text-sm">
          {(['faqs', 'sheet', 'upsells'] as const).map(t => (
            <button key={t} type="button" onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg border ${tab === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
              {t === 'faqs' ? `FAQs (${faqs.length})` : t === 'sheet' ? `Questions asked (${rows.length})` : `Upsells (${upsells.length})`}
            </button>
          ))}
        </div>
      </div>

      {tab === 'faqs' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={readOnly || build.isLoading} onClick={() => build.mutate()} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${build.isLoading ? 'animate-spin' : ''}`} /> {build.isLoading ? 'Reading the questions…' : faqs.length ? 'Rebuild from the questions' : 'Build the FAQs from the questions'}
            </button>
            <button type="button" disabled={readOnly} onClick={() => { setAdding(true); setEditing(null); }} className="btn-secondary text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Add one by hand</button>
            <p className="text-xs text-gray-600">{faqData?.ai ? 'Claude reads the sheet and groups what people keep asking; answers come from the replies you sent.' : 'No AI key on the server: the questions are grouped by topic instead.'} An FAQ you edited is kept on the next build; a built one you never touched is replaced.</p>
          </div>
          {adding && <Editor value={{ id: null, question: '', answer: '' }} busy={save.isLoading} onSave={d => save.mutate(d)} onCancel={() => setAdding(false)} />}
          {faqs.length === 0 && !adding && <p className="text-sm text-gray-600">No FAQs yet. Tap Build — the sheet has {rows.length} question{rows.length === 1 ? '' : 's'} to read.</p>}
          <ul className="space-y-2">
            {faqs.map(f => (
              <li key={f.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
                {editing?.id === f.id ? <Editor value={editing} busy={save.isLoading} onSave={d => save.mutate(d)} onCancel={() => setEditing(null)} /> : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">{f.question}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {f.count > 0 && <span className="text-[11px] text-gray-500 whitespace-nowrap">asked {f.count}×</span>}
                        {!readOnly && <button type="button" onClick={() => { setEditing({ id: f.id, question: f.question, answer: f.answer }); setAdding(false); }} className="p-1 text-gray-500 hover:text-brand-600"><Pencil className="h-3.5 w-3.5" /></button>}
                        {!readOnly && <button type="button" onClick={() => { if (window.confirm('Remove this FAQ?')) remove.mutate(f.id); }} className="p-1 text-gray-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    </div>
                    <p className={`text-sm mt-1 whitespace-pre-wrap ${/^TODO:/.test(f.answer) ? 'text-amber-800' : 'text-gray-700'}`}>{f.answer || <span className="text-amber-800">No answer yet — tap the pencil and write one.</span>}</p>
                    <p className="text-[11px] text-gray-500 mt-1">{f.source === 'hand' ? 'Written by hand' : f.edited_at ? 'Built, then edited by you' : 'Built from the questions'}</p>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'sheet' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={days} onChange={e => setDays(Number(e.target.value))} className="input text-sm w-40">
              <option value={90}>Last 90 days</option><option value={365}>Last year</option><option value={3650}>All time</option>
            </select>
            <button type="button" onClick={downloadCsv} className="btn-secondary text-sm flex items-center gap-1.5"><Download className="h-4 w-4" /> Download the sheet (CSV)</button>
            <p className="text-xs text-gray-600">Every question, request or complaint a customer texted (or typed on their page), what the app understood, and the first reply we sent. Read from <Link to="/texts" className="underline">Texts</Link>.</p>
          </div>
          <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Who</th><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">What they asked</th><th className="text-left px-3 py-2">What we replied</th><th className="text-left px-3 py-2">Status</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.id} className="align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{day(r.asked_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.customer_id ? <Link to={`/customers?profile=${r.customer_id}`} className="text-brand-700 underline">{r.customer_name || r.phone_pretty}</Link> : r.phone_pretty}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{KIND_WORD[r.kind] || r.kind}</td>
                    <td className="px-3 py-2 max-w-md"><p className="text-gray-900">{r.question}</p>{r.summary && <p className="text-[11px] text-gray-500">{r.summary}</p>}</td>
                    <td className="px-3 py-2 max-w-md text-gray-700">{r.answer ? r.answer.replace(/^KeepAustinMowed:\s*/i, '') : <span className="text-amber-800">no reply on record</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.status}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No questions on record for this span.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'upsells' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-600">Every "They wanted" the crew sent in from the job (the winter-upsell photos), newest first. Each one is a reminder on the customer's card and on My Day → Reach out; this is the whole list.</p>
          <ul className="space-y-2">
            {upsells.map(u => (
              <li key={u.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Link to={`/customers?profile=${u.customer_id}`} className="text-sm font-semibold text-brand-700 underline">{u.customer_name || u.name || `Customer #${u.customer_id}`}</Link>
                    {u.address && <span className="text-xs text-gray-500 ml-2">{u.address}</span>}
                    <p className="text-sm text-gray-800 mt-0.5">{u.note}</p>
                  </div>
                  <span className={`text-[11px] whitespace-nowrap ${u.status === 'open' ? 'text-amber-800' : 'text-gray-500'}`}>{u.status === 'open' ? 'open' : u.status} · {day(u.created_at)}</span>
                </div>
                {u.photo_urls && u.photo_urls.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {u.photo_urls.map((p, i) => <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt="" className="h-20 w-20 object-cover rounded-lg border border-gray-200" /></a>)}
                  </div>
                )}
              </li>
            ))}
            {upsells.length === 0 && <li className="text-sm text-gray-600 flex items-center gap-2"><Camera className="h-4 w-4" /> No upsells sent in yet.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
